import React, { useState, useEffect, useMemo } from "react";
import {
  Card,
  Badge,
  Spinner,
  Alert,
  Table,
  Button,
  Row,
  Col,
  InputGroup,
  Form,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import {
  IconBuildingStore,
  IconSearch,
  IconFilter,
  IconEye,
  IconEdit,
  IconTrash,
  IconAlertTriangle,
  IconPlus,
  IconCheck,
  IconX,
  IconPlayerPause,
  IconRefresh,
  IconTrendingUp,
  IconTrendingDown,
  IconDeviceAnalytics,
  IconChartPie,
} from "@tabler/icons-react";
import Swal from "sweetalert2";
import PublicationsServices from "./services/PublicationsServices";

const PublicationsList = () => {
  const [loading, setLoading] = useState(false);
  const [publications, setPublications] = useState([]);
  const [statusSummary, setStatusSummary] = useState({
    pending: 0,
    published: 0,
    suspended: 0,
    removed: 0,
    total: 0,
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 10;

  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    loadPublications();
    loadStatusSummary();
  }, [currentPage, searchTerm, statusFilter]);

  const loadPublications = async () => {
    try {
      setLoading(true);

      const filters = {
        page: currentPage,
        page_size: pageSize,
        search: searchTerm || undefined,
        status: statusFilter !== "all" ? statusFilter : undefined,
      };

      const params = PublicationsServices.buildSearchParams(filters);
      const response = await PublicationsServices.listPublications(params);

      setPublications(response.data.results || []);
      setTotalPages(Math.ceil(response.data.count / pageSize) || 1);
      setTotalCount(response.data.count || 0);
    } catch (error) {
      console.error("Erro ao carregar publicações:", error);
      Swal.fire({
        icon: "error",
        title: "Erro",
        text: "Não foi possível carregar as publicações.",
        confirmButtonColor: "#dc3545",
      });
    } finally {
      setLoading(false);
    }
  };

  const loadStatusSummary = async () => {
    try {
      const response = await PublicationsServices.getStatusSummary();
      setStatusSummary(response.data);
    } catch (error) {
      console.error("Erro ao carregar resumo de status:", error);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    Promise.all([loadPublications(), loadStatusSummary()]).finally(() => {
      setRefreshing(false);
    });
  };

  const handleSearch = (value) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const handleStatusFilter = (status) => {
    setStatusFilter(status);
    setCurrentPage(1);
  };

  // Status cards data
  const statusCards = useMemo(
    () => [
      {
        id: "published",
        icon: IconCheck,
        value: statusSummary.published,
        label: "Publicados",
        description: "Ativos no marketplace",
        trend: "positive",
        color: "linear-gradient(135deg, #2bc155, #20c997)",
        bgColor: "success",
      },
      {
        id: "pending",
        icon: IconDeviceAnalytics,
        value: statusSummary.pending,
        label: "Pendentes",
        description: "Aguardando aprovação",
        trend: "neutral",
        color: "linear-gradient(135deg, #137ff7, #1ea7c5)",
        bgColor: "primary",
      },
      {
        id: "suspended",
        icon: IconPlayerPause,
        value: statusSummary.suspended,
        label: "Suspensos",
        description: "Temporariamente inativos",
        trend: "negative",
        color: "linear-gradient(135deg, #ffc107, #ff8c00)",
        bgColor: "warning",
      },
      {
        id: "removed",
        icon: IconX,
        value: statusSummary.removed,
        label: "Removidos",
        description: "Removidos do marketplace",
        trend: "negative",
        color: "linear-gradient(135deg, #dc3545, #c82333)",
        bgColor: "danger",
      },
    ],
    [statusSummary]
  );

  const getStatusVariant = (status) => {
    const variants = {
      published: "success",
      pending: "primary",
      suspended: "warning",
      removed: "danger",
      rejected: "secondary",
    };
    return variants[status] || "secondary";
  };

  const getStatusText = (status) => {
    const texts = {
      published: "Publicado",
      pending: "Pendente",
      suspended: "Suspenso",
      removed: "Removido",
      rejected: "Rejeitado",
    };
    return texts[status] || status;
  };

  const handleViewDetails = (publication) => {
    Swal.fire({
      title: "Detalhes da Publicação",
      html: `
        <div style="text-align: left;">
          <p><strong>Agente:</strong> ${publication.agent_name}</p>
          <p><strong>Área:</strong> ${publication.agent_area}</p>
          <p><strong>ID do Marketplace:</strong> ${
            publication.marketplace_product_id
          }</p>
          ${
            publication.marketplace_url
              ? `<p><strong>URL:</strong> <a href="${publication.marketplace_url}" target="_blank">${publication.marketplace_url}</a></p>`
              : ""
          }
          <p><strong>Status:</strong> ${publication.status_display}</p>
          ${
            publication.published_at
              ? `<p><strong>Publicado em:</strong> ${new Date(
                  publication.published_at
                ).toLocaleString("pt-BR")}</p>`
              : ""
          }
          ${
            publication.rejection_reason
              ? `<p><strong>Motivo da Rejeição:</strong> ${publication.rejection_reason}</p>`
              : ""
          }
          <p><strong>Criado em:</strong> ${new Date(
            publication.created_at
          ).toLocaleString("pt-BR")}</p>
          <p><strong>Atualizado em:</strong> ${new Date(
            publication.updated_at
          ).toLocaleString("pt-BR")}</p>
        </div>
      `,
      width: 600,
      confirmButtonText: "Fechar",
      confirmButtonColor: "#007bff",
    });
  };

  const handlePublish = async (publication) => {
    const result = await Swal.fire({
      title: "Publicar Agente",
      text: `Deseja marcar "${publication.agent_name}" como publicado?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Sim, publicar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#28a745",
    });

    if (!result.isConfirmed) return;

    try {
      await PublicationsServices.publishPublication(publication.id);

      Swal.fire({
        icon: "success",
        title: "Sucesso!",
        text: "Publicação marcada como publicada.",
        timer: 2000,
        showConfirmButton: false,
      });

      loadPublications();
      loadStatusSummary();
    } catch (error) {
      console.error("Erro ao publicar:", error);
      Swal.fire({
        icon: "error",
        title: "Erro",
        text:
          error.response?.data?.error ||
          "Não foi possível publicar a publicação.",
        confirmButtonColor: "#dc3545",
      });
    }
  };

  const handleReject = async (publication) => {
    const { value: reason } = await Swal.fire({
      title: "Rejeitar Publicação",
      text: `Deseja rejeitar "${publication.agent_name}"?`,
      input: "textarea",
      inputLabel: "Motivo da rejeição (opcional)",
      inputPlaceholder: "Digite o motivo da rejeição...",
      showCancelButton: true,
      confirmButtonText: "Rejeitar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#dc3545",
    });

    if (reason === undefined) return;

    try {
      await PublicationsServices.rejectPublication(publication.id, reason);

      Swal.fire({
        icon: "success",
        title: "Sucesso!",
        text: "Publicação rejeitada com sucesso.",
        timer: 2000,
        showConfirmButton: false,
      });

      loadPublications();
      loadStatusSummary();
    } catch (error) {
      console.error("Erro ao rejeitar:", error);
      Swal.fire({
        icon: "error",
        title: "Erro",
        text:
          error.response?.data?.error ||
          "Não foi possível rejeitar a publicação.",
        confirmButtonColor: "#dc3545",
      });
    }
  };

  const handleSuspend = async (publication) => {
    const result = await Swal.fire({
      title: "Suspender Publicação",
      text: `Deseja suspender "${publication.agent_name}"?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sim, suspender",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#ffc107",
    });

    if (!result.isConfirmed) return;

    try {
      await PublicationsServices.suspendPublication(publication.id);

      Swal.fire({
        icon: "success",
        title: "Sucesso!",
        text: "Publicação suspensa com sucesso.",
        timer: 2000,
        showConfirmButton: false,
      });

      loadPublications();
      loadStatusSummary();
    } catch (error) {
      console.error("Erro ao suspender:", error);
      Swal.fire({
        icon: "error",
        title: "Erro",
        text:
          error.response?.data?.error ||
          "Não foi possível suspender a publicação.",
        confirmButtonColor: "#dc3545",
      });
    }
  };

  const handleRemove = async (publication) => {
    const result = await Swal.fire({
      title: "Remover Publicação",
      text: `Deseja remover "${publication.agent_name}" do marketplace?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sim, remover",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#dc3545",
    });

    if (!result.isConfirmed) return;

    try {
      await PublicationsServices.removePublication(publication.id);

      Swal.fire({
        icon: "success",
        title: "Sucesso!",
        text: "Publicação removida com sucesso.",
        timer: 2000,
        showConfirmButton: false,
      });

      loadPublications();
      loadStatusSummary();
    } catch (error) {
      console.error("Erro ao remover:", error);
      Swal.fire({
        icon: "error",
        title: "Erro",
        text:
          error.response?.data?.error ||
          "Não foi possível remover a publicação.",
        confirmButtonColor: "#dc3545",
      });
    }
  };

  const handleDelete = async (publication) => {
    const result = await Swal.fire({
      title: "Confirmar Exclusão",
      text: `Deseja excluir permanentemente a publicação "${publication.agent_name}"?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sim, excluir",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#dc3545",
    });

    if (!result.isConfirmed) return;

    try {
      await PublicationsServices.deletePublication(publication.id);

      Swal.fire({
        icon: "success",
        title: "Sucesso!",
        text: "Publicação excluída com sucesso.",
        timer: 2000,
        showConfirmButton: false,
      });

      loadPublications();
      loadStatusSummary();
    } catch (error) {
      console.error("Erro ao excluir:", error);
      Swal.fire({
        icon: "error",
        title: "Erro",
        text:
          error.response?.data?.error ||
          "Não foi possível excluir a publicação.",
        confirmButtonColor: "#dc3545",
      });
    }
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pages = [];
    const maxVisible = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);

    if (endPage - startPage + 1 < maxVisible) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <Button
          key={i}
          variant={i === currentPage ? "primary" : "outline-primary"}
          size="sm"
          className="me-1"
          onClick={() => setCurrentPage(i)}
        >
          {i}
        </Button>
      );
    }

    return (
      <div className="d-flex justify-content-center align-items-center mt-3">
        <Button
          variant="outline-primary"
          size="sm"
          className="me-2"
          disabled={currentPage === 1}
          onClick={() => setCurrentPage(currentPage - 1)}
        >
          Anterior
        </Button>
        {pages}
        <Button
          variant="outline-primary"
          size="sm"
          className="ms-2"
          disabled={currentPage === totalPages}
          onClick={() => setCurrentPage(currentPage + 1)}
        >
          Próximo
        </Button>
      </div>
    );
  };

  return (
    <div className="publications-screen">
      <div className="page-header mb-4">
        <div className="d-flex justify-content-between align-items-center w-100">
          <div>
            <h3 className="d-flex">
              <IconBuildingStore size={30} className="me-2" />
              Publicações no Marketplace
            </h3>
            <p className="text-muted">
              Gerencie as publicações dos seus agentes no marketplace
            </p>
          </div>
          <div className="d-flex gap-2">
            <Button
              variant="outline-primary"
              onClick={handleRefresh}
              disabled={refreshing}
            >
              {refreshing ? (
                <Spinner size="sm" className="me-1" />
              ) : (
                <IconRefresh size={16} className="me-1" />
              )}
              Atualizar
            </Button>
          </div>
        </div>
      </div>

      {/* Status Cards */}
      <section className="metrics-section mb-4">
        <div className="dashboard-grid grid-4">
          {statusCards.map((card, index) => {
            const IconComponent = card.icon;
            return (
              <div
                key={card.id}
                className="metric-card fade-in"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="metric-header">
                  <div
                    className="metric-icon"
                    style={{ background: card.color }}
                  >
                    <IconComponent size={20} />
                  </div>
                  <div className={`metric-trend ${card.trend}`}>
                    {card.trend === "positive" ? (
                      <IconTrendingUp size={10} />
                    ) : card.trend === "negative" ? (
                      <IconTrendingDown size={10} />
                    ) : (
                      <IconChartPie size={10} />
                    )}
                  </div>
                </div>
                <div className="metric-value">{card.value}</div>
                <h3 className="metric-label">{card.label}</h3>
                <p className="metric-description">{card.description}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Filtros e Busca */}
      <Card className="mb-4">
        <Card.Body>
          <Row>
            <Col xs={12} md={4}>
              <InputGroup>
                <InputGroup.Text>
                  <IconSearch size={16} />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Buscar por agente ou ID do marketplace..."
                  value={searchTerm}
                  onChange={(e) => handleSearch(e.target.value)}
                />
              </InputGroup>
            </Col>
            <Col xs={12} md={3}>
              <InputGroup>
                <InputGroup.Text>
                  <IconFilter size={16} />
                </InputGroup.Text>
                <Form.Select
                  value={statusFilter}
                  onChange={(e) => handleStatusFilter(e.target.value)}
                >
                  <option value="all">Todos os Status</option>
                  <option value="pending">Pendentes</option>
                  <option value="published">Publicados</option>
                  <option value="suspended">Suspensos</option>
                  <option value="removed">Removidos</option>
                </Form.Select>
              </InputGroup>
            </Col>
            <Col xs={12} md={5}>
              <div className="text-muted small d-flex align-items-center h-100">
                Total: {totalCount} publicações
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Tabela de Publicações */}
      <Card>
        <Card.Header className="d-flex gap-2 align-items-center">
          <IconBuildingStore size={20} />
          <h5 className="mb-0">Lista de Publicações</h5>
        </Card.Header>
        <Card.Body>
          {loading ? (
            <div
              className="d-flex justify-content-center align-items-center"
              style={{ minHeight: "200px" }}
            >
              <div className="text-center">
                <Spinner animation="border" variant="primary" />
                <p className="mt-3">Carregando publicações...</p>
              </div>
            </div>
          ) : publications.length > 0 ? (
            <>
              <Table responsive hover className="publications-table">
                <thead>
                  <tr>
                    <th>Agente</th>
                    <th>ID Marketplace</th>
                    <th>Status</th>
                    <th>Publicado em</th>
                    <th>Criado em</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {publications.map((publication) => (
                    <tr key={publication.id}>
                      <td>
                        <div>
                          <div className="fw-bold">
                            {publication.agent_name}
                          </div>
                          <small className="text-muted">
                            {publication.agent_area}
                          </small>
                        </div>
                      </td>
                      <td>
                        <div>
                          <div className="fw-bold">
                            {publication.marketplace_product_id}
                          </div>
                          {publication.marketplace_url && (
                            <small>
                              <a
                                href={publication.marketplace_url}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                Ver no marketplace
                              </a>
                            </small>
                          )}
                        </div>
                      </td>
                      <td>
                        <Badge bg={getStatusVariant(publication.status)}>
                          {getStatusText(publication.status)}
                        </Badge>
                        {publication.rejection_reason && (
                          <div>
                            <small className="text-danger">
                              <IconAlertTriangle size={12} className="me-1" />
                              {publication.rejection_reason}
                            </small>
                          </div>
                        )}
                      </td>
                      <td>
                        {publication.published_at ? (
                          <span className="date-info">
                            {new Date(
                              publication.published_at
                            ).toLocaleDateString("pt-BR")}
                          </span>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      <td>
                        <span className="date-info">
                          {new Date(publication.created_at).toLocaleDateString(
                            "pt-BR"
                          )}
                        </span>
                      </td>
                      <td>
                        <div className="d-flex gap-1">
                          <OverlayTrigger
                            overlay={<Tooltip>Ver detalhes</Tooltip>}
                          >
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => handleViewDetails(publication)}
                            >
                              <IconEye size={14} />
                            </Button>
                          </OverlayTrigger>

                          {publication.status === "published" && (
                            <OverlayTrigger
                              overlay={<Tooltip>Suspender</Tooltip>}
                            >
                              <Button
                                variant="outline-warning"
                                size="sm"
                                onClick={() => handleSuspend(publication)}
                              >
                                <IconPlayerPause size={14} />
                              </Button>
                            </OverlayTrigger>
                          )}

                          {["published", "suspended"].includes(
                            publication.status
                          ) && (
                            <OverlayTrigger
                              overlay={
                                <Tooltip>Remover do marketplace</Tooltip>
                              }
                            >
                              <Button
                                variant="outline-warning"
                                size="sm"
                                onClick={() => handleRemove(publication)}
                              >
                                <IconX size={14} />
                              </Button>
                            </OverlayTrigger>
                          )}

                          <OverlayTrigger
                            overlay={<Tooltip>Excluir permanentemente</Tooltip>}
                          >
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => handleDelete(publication)}
                            >
                              <IconTrash size={14} />
                            </Button>
                          </OverlayTrigger>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              {renderPagination()}
            </>
          ) : (
            <Alert variant="info" className="text-center">
              <IconBuildingStore size={24} className="mb-2" />
              <p className="mb-0">
                {searchTerm || statusFilter !== "all"
                  ? "Nenhuma publicação encontrada com os filtros aplicados."
                  : "Nenhuma publicação encontrada."}
              </p>
              <small>
                {searchTerm || statusFilter !== "all"
                  ? "Tente ajustar os filtros de busca."
                  : "Publique seu primeiro agente no marketplace para começar."}
              </small>
            </Alert>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default PublicationsList;
